import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import ConfirmationDialog from '@sayyesschool/frontend/components/confirmation-dialog';
import FormDialog from '@sayyesschool/frontend/components/form-dialog';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import Page from '@sayyesschool/frontend/components/page';
import { useLearnerActions } from '@sayyesschool/frontend/features/learners';
import { useManagers } from '@sayyesschool/frontend/features/managers';
import { useRequest, useRequests } from '@sayyesschool/frontend/features/requests';
import { useUser } from '@sayyesschool/frontend/features/user';
import { useBoolean } from '@sayyesschool/frontend/hooks/state';
import { useNotificationActions } from '@sayyesschool/frontend/store/features/notification';
import { Link } from '@sayyesschool/frontend/ui-components';

import RequestForm from '@/components/requests/request-form';
import RequestProcessFormDialog from '@/components/requests/request-process-form-dialog';
import RequestsSearch from '@/components/requests/requests-search';
import RequestsTable from '@/components/requests/requests-table';

export default function RequestsPage({ history }) {
    const [user] = useUser();
    const [managers] = useManagers();
    const learnerActions = useLearnerActions();
    const [requests, requestActions] = useRequests();
    const [request] = useRequest();
    const { showNotification } = useNotificationActions();

    const [searchParams, setSearchParams] = useState();
    const [isLoading, setLoading] = useState(false);
    const [isRequestProcessPanelOpen, toggleRequestProcessPanelOpen] = useBoolean(false);
    const [isRequestFormOpen, toggleRequestFormOpen] = useBoolean(false);
    const [matchedLearner, setMatchedLearner] = useState();

    const submitRequest = useCallback(data => {
        if (!request) return;

        return requestActions.processRequest(request.id, data)
            .then(({ data }) => {
                toggleRequestProcessPanelOpen(false);
                history.push(`/learners/${data.learnerId}`);
            });
    }, [
        request,
        requestActions,
        toggleRequestProcessPanelOpen,
        history
    ]);

    const processRequest = useCallback(request => {
        if (!request.managerId) {
            requestActions.updateRequest(request.id, {
                status: 'processing',
                managerId: user.id
            }).then(({ data: request }) => processRequest(request));
        } else if (request.managerId === user.id) {
            requestActions.setRequest(request);
            toggleRequestProcessPanelOpen(true);
        } else {
            showNotification({ text: `Обработкой заявки уже занимается ${request.manager.fullname}` });
        }
    }, [user, requestActions, toggleRequestProcessPanelOpen, showNotification]);

    const checkRequest = useCallback(request => {
        requestActions.setRequest(request);

        if (request.status === 'processing') {
            return processRequest(request);
        }

        return learnerActions.getLearners({
            email: request.contact.email,
            phones: request.contact.phone
        }).then(({ data: learners }) => {
            if (learners.length > 0) {
                setMatchedLearner(learners[0]);
            } else {
                processRequest(request);
            }
        });
    }, [requestActions, learnerActions, processRequest]);

    const handleLearnerMatch = useCallback(() => {
        return requestActions.updateRequest(request.id, {
            status: 'processing',
            managerId: user.id,
            learnerId: matchedLearner.id
        }).then(({ data: request }) => {
            setMatchedLearner(undefined);
            showNotification({ text: 'Заявка привязана к клиенту' });
            processRequest(request);
        });
    }, [
        user,
        request,
        matchedLearner,
        requestActions,
        showNotification,
        processRequest
    ]);

    const dismissLearnerMatch = useCallback(() => {
        setMatchedLearner(undefined);
        processRequest(request);
    }, [request, setMatchedLearner, processRequest]);

    const editRequest = useCallback(request => {
        requestActions.setRequest(request);
        toggleRequestFormOpen(true);
    }, [requestActions, toggleRequestFormOpen]);

    const createRequest = useCallback(data => {
        return requestActions.createRequest(data)
            .then(() => toggleRequestFormOpen(false));
    }, [requestActions, toggleRequestFormOpen]);

    const updateRequest = useCallback(data => {
        return requestActions.updateRequest(data.id, data)
            .then(() => toggleRequestFormOpen(false));
    }, [requestActions, toggleRequestFormOpen]);

    const deleteRequest = useCallback(request => {
        return requestActions.deleteRequest(request.id)
            .then(() => requestActions.unsetRequest());
    }, [requestActions]);

    const search = useCallback(params => {
        setSearchParams(prevParams => ({ ...prevParams, ...params }));

        setLoading(true);

        return requestActions.getRequests(params).finally(() => setLoading(false));
    }, [requestActions]);

    const clearSearch = useCallback(() => {
        setSearchParams();

        setLoading(true);

        return requestActions.getRequests().finally(() => setLoading(false));
    }, [requestActions]);

    const closeRequestForm = useCallback(() => {
        requestActions.unsetRequest();
        toggleRequestFormOpen(false);
    }, [requestActions, toggleRequestFormOpen]);

    const emptyRequest = useMemo(() => ({ managerId: user.id }), [user?.id]);

    if (!requests) return <LoadingIndicator />;

    return (
        <Page className="RequestsPage">
            <Page.Header
                title="Заявки"
                actions={[
                    {
                        key: 'export',
                        as: 'a',
                        href: '/api/requests/export' + (searchParams ? `?${new URLSearchParams(searchParams).toString()}` : ''),
                        download: true,
                        icon: 'file_export',
                        content: 'Экспорт в CSV',
                        variant: 'plain'
                    },
                    {
                        key: 'new',
                        icon: 'add',
                        content: 'Создать заявку',
                        variant: 'solid',
                        onClick: () => toggleRequestFormOpen(true)
                    }
                ]}
                loading={isLoading}
            >
                <RequestsSearch
                    onSubmit={search}
                    onClear={clearSearch}
                />
            </Page.Header>

            <Page.Content>
                <Page.Section
                    variant="outlined"
                    compact
                    scrollable
                >
                    <RequestsTable
                        requests={requests}
                        manager={user}
                        onEdit={editRequest}
                        onProcess={checkRequest}
                        onDelete={deleteRequest}
                    />
                </Page.Section>
            </Page.Content>

            {request &&
                <RequestProcessFormDialog
                    request={request}
                    open={isRequestProcessPanelOpen}
                    onSubmit={submitRequest}
                    onClose={toggleRequestProcessPanelOpen}
                />
            }

            <FormDialog
                title={request ? 'Редактирование заявки' : 'Создание заявки'}
                open={isRequestFormOpen}
                submitButtonText={request ? 'Сохранить' : 'Создать'}
                onClose={closeRequestForm}
            >
                <RequestForm
                    id="request-form"
                    request={request || emptyRequest}
                    managers={managers}
                    onSubmit={request ? updateRequest : createRequest}
                />
            </FormDialog>

            <ConfirmationDialog
                title="Совпадение номера телефона"
                open={!!matchedLearner}
                onConfirm={handleLearnerMatch}
                onClose={dismissLearnerMatch}
            >
                Номер телефона или email в заявке совпадает с данными существующего клиента <Link
                    as={RouterLink}
                    to={matchedLearner?.url}
                    color="primary"
                    target="_blank"
                >{matchedLearner?.fullname}</Link>. Вы хотите привязать заявку к нему?
            </ConfirmationDialog>
        </Page>
    );
}